import { useEffect, useRef } from 'react';
import { FaGlobeAsia, FaSchool, FaUserGraduate } from 'react-icons/fa';
import appConfig from '../config/appConfig';

function Main() {
  const taglineRef = useRef(null);

  // Typewriter effect for tagline
  useEffect(() => {
    const text = appConfig.company.tagline || 'Your journey to education in Japan'; // Fallback tagline
    const el = taglineRef.current;
    let index = 0;
    let currentText = '';
    const speed = 100;

    // Clear any existing content
    el.textContent = '';

    // Handle Japanese (or any) characters correctly
    const chars = [...text]; // Spread to handle multi-byte characters (e.g., 日本語)

    function type() {
      if (index < chars.length) {
        currentText += chars[index];
        el.textContent = currentText;
        index++;
        setTimeout(type, speed);
      }
    }

    // Start typing only if element exists
    if (el) {
      type();
    }

    // Cleanup to prevent multiple timers
    return () => {
      index = chars.length; // Stop typing on unmount
    };
  }, []);

  return (
    <>
      {/* Hero Section */}
      <header
        id="home"
        role="banner"
        aria-label="Your journey to education and a bright future in Japan"
        className="text-white text-center d-flex flex-column justify-content-center align-items-center"
        style={{
          background: 'linear-gradient(135deg, var(--primary-brand-dark), var(--primary-brand-light) 80%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          overflow: 'hidden',
          minHeight: '100vh',
        }}
      >
        {/* Background Overlay */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.15)', // Very light overlay to highlight tide and mountains
            zIndex: 1,
          }}
        ></div>

        {/* Animated SVG Background (Lake Tide with Japanese Wave) */}
        <div className="hero-background-pattern">
          <svg
            width="100%"
            height="100%"
            preserveAspectRatio="xMidYMid slice"
            style={{ position: 'absolute', zIndex: 0, opacity: 0.3 }}
          >
            <defs>
              <pattern id="tide" x="0" y="0" width="200" height="100" patternUnits="userSpaceOnUse">
                <path
                  d="M0 70 C 50 40, 150 60, 200 30 C 250 0, 350 50, 400 70"
                  fill="none"
                  stroke="var(--hero-flag-japan-red)" // Japanese red for cultural touch
                  strokeWidth="3"
                />
              </pattern>
              <pattern id="mountain" x="0" y="0" width="100%" height="100%" patternUnits="userSpaceOnUse">
                <path
                  d="M0 80 L50 60 L100 75 L150 55 L200 70 L250 50 L300 65 L350 45 L400 60 L450 50 L500 70 V100 H0 Z"
                  fill="var(--primary-brand-dark)" // Dark teal for mountain silhouette
                  opacity="0.2"
                />
              </pattern>
            </defs>
            <rect x="0" y="0" width="100%" height="100%" fill="url(#mountain)" />
            <rect x="0" y="0" width="100%" height="100%" fill="url(#tide)" />
          </svg>
        </div>

        <div className="container position-relative" style={{ zIndex: 2 }}>
          {/* Company Name */}
          <h1
            className="fw-bold mb-3 animate__animated animate__fadeInDown text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl"
            style={{ fontFamily: "'Poppins', sans-serif", color: 'var(--text-color-white)' }}
          >
            Shikitari Japanese Language Academy
          </h1>
          {/* Tagline with Typewriter Effect */}
          <p
            ref={taglineRef}
            className="lead mb-4 fs-4 fw-semibold animate__animated animate__fadeIn"
            style={{ fontFamily: "'Roboto', sans-serif", color: 'var(--primary-brand-accent)' }}
          ></p>

          {/* Call to Action Buttons */}
          <div className="d-flex justify-content-center gap-3 mb-5">
            <a
              href="#contact"
              className="btn btn-lg px-5 shadow badass-button"
              style={{
                 background: 'linear-gradient(15deg, var(--primary-brand-), var(--primary-brand-light))', // Blue to light cyan
                color: 'var(--text-color-white)',
                border: 'none',
                borderRadius: '150px',
                padding: '12px 30px',
                fontWeight: '600',
              }}
            >
              Get in Touch
            </a>
          </div>

          {/* Highlight Info Cards */}
          <div className="row justify-content-center mt-4">
            <Highlight
              icon={<FaUserGraduate size={40} color="var(--text-color-white)" />}
              number="Starting..."
              text="Students Sent"
            />
            <Highlight
              icon={<FaSchool size={40} color="var(--text-color-white)" />}
              number="10+"
              text="Partner Schools"
            />
            <Highlight
              icon={<FaGlobeAsia size={40} color="var(--text-color-white)" />}
              number="10+"
              text="Years in Japan"
            />
          </div>
        </div>
      </header>
    </>
  );
}

// Highlight info card for hero section
function Highlight({ icon, number, text }) {
  return (
    <div className="col-6 col-md-4 mb-3 d-flex justify-content-center align-items-center">
      <div
        className="p-3 shadow-sm d-flex flex-column justify-content-center align-items-center highlight-card"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(5, 81, 96, 0.3))', // Matches dark teal
          border: '1px solid var(--primary-brand-accent)',
          width: '160px',
          height: '160px',
          borderRadius: '50%',
          color: 'var(--text-color-white)',
          textAlign: 'center',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        }}
      >
        <div className="mb-2">{icon}</div>
        <h5 className="fw-bold mb-1" style={{ fontSize: '1.2rem', fontFamily: "'Poppins', sans-serif" }}>
          <span className="counter" data-target={number.replace(/[^0-9]/g, '')}>{number}</span>
        </h5>
        <p className="mb-0" style={{ fontSize: '0.9rem', fontFamily: "'Roboto', sans-serif" }}>{text}</p>
      </div>
    </div>
  );
}

export default Main;